import { useEffect, useMemo, useState } from 'react';
import {useLocation, useParams, useNavigate} from 'react-router';
import {useDebounceFn, useMemoizedFn} from 'ahooks';
import Bus from '@/utils/bus';
import AIContant from '@/utils/aiContant';
import global from '@/utils/global';
import { IClearHighlightPayload, IDeleteNotePayload, ISaveAnswerHandle, IUpdatePanel} from '@/utils/ctype';
import { IhightLightItem } from '@/utils/ctype';
import { clearAudioHandle } from '@/utils/audioPlay';
import { logCount, logError, logInfo } from '@/utils/logger/arms-config';
import { login } from '@/utils/login';
import {useAppSelector} from '@/stores/index';
import { useAppDispatch } from '@/stores/index';
import {getListeningContentAsync, setPanelDataByIndex, setFocusIndex, saveAnswer,
  getAnswerListAsync, saveHighlightData, updateTimeState,
  saveAnswerListAsync, updateRecordId, submitAnswerAsync, toggleShowNotePanel, clearHightLightInfo,
  updatePanelInfo, setIsSubject, setShowDragFiled,
  getListeningFeedbackAsync, deleteNote} from '@/stores/slice/listening';
import { getStudentInfoAsync } from '@/stores/slice/user';
import { getExamInfoAsync } from '@/stores/slice/exam';
import { useModulePoints } from '@/hooks/useModulePoints';
import NotePanel from '../../components/NotePanel';
import Bottom from '../../components/Bottom';
import Main from '../../components/Main';
import { AIPanel } from '../../components/AIPanel';
import Loading from '../../components/Loading';
import MainContent from './MainContent';
import style from './index.module.less';
import Article from './Article';

export default function MockListening() {

  global.subject = 'listen';
  const params = useParams();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const showNotePanel = useAppSelector(state => state.listening.showNotePanel);
  const answerAll = useAppSelector(state => state.listening.answerAll);
  const answerMap = useAppSelector(state => state.listening.answerMap);
  const displayAnswerMap = useAppSelector(state => state.listening.displayAnswerMap);
  const limits = useAppSelector(state => state.listening.limits);
  const currentFocus = useAppSelector(state => state.listening.currentFocus);
  const pageDataIndex = useAppSelector(state => state.listening.pageDataIndex);
  const loadAnswered = useAppSelector(state => state.listening.loadAnswered);
  const pageDataCache = useAppSelector(state => state.listening.pageDataCache);
  const highLightInfo = useAppSelector(state => state.listening.highLightInfo);
  const surplus_time = useAppSelector(state => state.listening.surplus_time);
  const recordId = useAppSelector(state => state.listening.recordId);
  const isSubject = useAppSelector(state => state.listening.isSubject);
  const showDragField = useAppSelector(state => state.listening.showDragField);
  const resultMap = useAppSelector(state => state.listening.resultMap);
  const location = useLocation();
  // ! isAI 的语义不够明确，应该使用的是 isResult 或者 isFeedback 这种含义更加准确的变量名
  const isAI = location.pathname.indexOf('/mocklisteningai') > -1;
  const currentPapeId = useAppSelector(state => state.listening.pageData?.question.paper_id);
  const status = useAppSelector(state => state.exam.info?.listening.status);
  const isLogin = useAppSelector(state => state.user.userPermission.isLogin);
  const inited = useAppSelector(state => state.user.inited);
  const isVip = useAppSelector(state => state.user.userPermission.isVip);
  const routeRecordId = Number(params.recordId);
  const [loadedRecordId, setLoadedRecordId] = useState<number | null>(null);
  const draftLoaded = loadedRecordId === routeRecordId;

  useEffect(() => {
    if (inited && !isLogin) {
      login();
    }
  }, [isLogin, inited]);

  useEffect(() => {
    console.log('页面前置检查', 'isAI:', isAI, 'status:', status);
    if (status === 3) {
      // 当前不应该打开页面
      console.log('跳回开始页');
      navigate(`/mockstart/${recordId}`);
      return;
    }

    if (isAI && (status === 1 || status === 2)) {
      console.log('跳回做题页');
      navigate(`/mocklistening/${recordId}`);
      return;
    }
    if (!isAI && status === 4) {
      console.log('跳回答案页');
      navigate(`/mocklisteningai/${recordId}`);
      return;
    }
  }, [isAI, status]);

  useEffect(() => {
    dispatch(updateRecordId(routeRecordId));
  }, [dispatch, routeRecordId]);
  
  useEffect(() => {
    if (!isAI && draftLoaded) {
      const timer = setInterval(() => {
        dispatch(saveAnswerListAsync());
      }, 10000);
      return () => {
        clearInterval(timer);
      };
    }
  }, [draftLoaded, isAI, dispatch]);
  const pointsInfo = useModulePoints(79);

  useEffect(() => {
    let active = true;
    let abortAnswerRequest: (() => void) | undefined;
    setLoadedRecordId(null);
    // 获取数据
    const contentRequest = dispatch(getListeningContentAsync({record_id: routeRecordId}));
    void (async () => {
      try {
        await contentRequest.unwrap();
        if (!active) {
          return;
        }
        const answerRequest = dispatch(getAnswerListAsync({record_id: routeRecordId}));
        abortAnswerRequest = answerRequest.abort;
        await answerRequest.unwrap();
        if (active) {
          setLoadedRecordId(routeRecordId);
        }
      } catch {
        // Keep saving disabled when the draft cannot be loaded.
      }
    })();
    dispatch(getExamInfoAsync({record_id: routeRecordId}));
    dispatch(getStudentInfoAsync());
    if (isAI) {
      dispatch(getListeningFeedbackAsync({ record_id: routeRecordId }));
    }
    return () => {
      active = false;
      contentRequest.abort();
      abortAnswerRequest?.();
    };
  }, [dispatch, isAI, routeRecordId]);

  const updateTimeHandle = useMemoizedFn((value: number) => {
    dispatch(updateTimeState(value));
  });
  
  const setPanelHandle = useMemoizedFn((index: number) => {
    dispatch(setPanelDataByIndex({index}));
    if (isAI) {
      Bus.publish(AIContant.AUDIO_STOP, undefined);
      clearAudioHandle();
    }
  });

  const setFocusIndexHandle = useMemoizedFn((index: number) => {
    dispatch(setFocusIndex({index}));
  });

  const { run, cancel, flush } = useDebounceFn(() => {
    if (draftLoaded) {
      dispatch(saveAnswerListAsync());
    }
  }, { wait: 1500, });
  useEffect(() => cancel, [cancel, routeRecordId]);
  const saveAnswerHandle: ISaveAnswerHandle = useMemoizedFn((index, value) => {
    if (!draftLoaded) {
      return;
    }
    if (Array.isArray(index) && Array.isArray(value)) {
      dispatch(saveAnswer({index, value}));
    } else if (!Array.isArray(index) && !Array.isArray(value)){
      dispatch(saveAnswer({index, value}));
    }
    run();
  });

  const saveHighlightDataHandle = useMemoizedFn((params: {show: boolean;wrapUid: string;} & IhightLightItem) => {
    if (!draftLoaded) {
      return;
    }
    dispatch(saveHighlightData(params));
    run();
  });

  const tabSubjectHandle = useMemoizedFn((val: boolean) => {
    dispatch(setIsSubject(val));
  });

  const [showLoading, setShowloading] = useState(false); 

  const submitHandle = useMemoizedFn((callback: () => void ) => {
    if (!draftLoaded) {
      return;
    }
    logCount('Submit_Answer_P1');
    logInfo({ submit: `Submit_Answer_State: showLoading${showLoading}`});

    if (showLoading) {
      console.log('[正在提交中。。。]');
      return;
    }
    setShowloading(true);
    logCount('Submit_Answer_P2');
    dispatch(saveAnswerListAsync()).then((res) => {
      logCount('Submit_Answer_P3');
      logInfo({ submit: `Submit_Answer_Question: ${JSON.stringify(res)}`});
      if ((res?.payload as {id: number;})?.id !== undefined) {
        logCount('Submit_Answer_P4');
        dispatch(submitAnswerAsync()).then((res2) => {
          logCount('Submit_Answer_P5');
          setShowloading(false);
          // setShowloading(false);
          if (res2?.payload) {
            logCount('Submit_Answer_P6');
            callback();
            navigate(`/mockstart/${recordId}`, {replace: true});
          }
        }).catch(err => {
          logCount('Submit_Answer_P7');
          setShowloading(false);
          logError(err);
        });
      }
    }).catch(err => {
      logError(err);
      logCount('Submit_Answer_P7');
      setShowloading(false);
      // isSubmit.current = false;
    });
  });

  const noteHandle = useMemoizedFn(() => {
    dispatch(toggleShowNotePanel());
  });

  const clearDialogHandle= useMemoizedFn((payload?: IClearHighlightPayload) => {
    if (!draftLoaded) {
      return;
    }
    dispatch(clearHightLightInfo(payload));
    run();
  });

  const updateDialogInfo = useMemoizedFn((info: IUpdatePanel) => {
    if (!draftLoaded) {
      return;
    }
    dispatch(updatePanelInfo(info));
    run();
    flush();
  });

  const deleteNoteHandle = useMemoizedFn((payload: IDeleteNotePayload) => {
    if (!draftLoaded) {
      return;
    }
    dispatch(deleteNote(payload));
    run();
  });

  const aiContent = useMemo(() => {

    return <AIPanel paper_id={currentPapeId} course='l' />;
  }, [currentPapeId]);

  const setShowDragFieldHandle = useMemoizedFn((val: boolean) => {
    dispatch(setShowDragFiled(val));
  });

  const node = useMemo(() => {
    return pageDataCache.map((pageDataItem, index: number) => {
      return (
        pageDataItem ? 
          <MainContent key={pageDataItem.question.paper_id} 
            question={pageDataItem.question.question} 
            currentFocus={currentFocus} 
            saveAnswerHandle={saveAnswerHandle}
            answerMap={answerMap}
            pageData={pageDataItem}
            loadAnswered={loadAnswered}
            feedback={resultMap}
            displayAnswerMap={displayAnswerMap}
            isAI={isAI}
            index={index}
            boxStyle={{display: pageDataIndex === index ? '' : 'none'}}
            showDragField={showDragField}
            setShowDragFieldHandle={setShowDragFieldHandle}
            setFocusIndexHandle={setFocusIndexHandle}></MainContent>
          : null
      );
    });
  }, [pageDataCache, answerMap, currentFocus, loadAnswered, displayAnswerMap, resultMap, isAI, pageDataIndex, showDragField]);

  return (
    <div className={style.viewBox}>
      <Main course="l" pageData={pageDataCache[pageDataIndex]} loadAnswered={loadAnswered} time={surplus_time}
        updateTimeHandle={updateTimeHandle} isAI={isAI} highLightObj={highLightInfo[pageDataIndex]}
        saveHighlightDataHandle={saveHighlightDataHandle} 
        tabSubjectHandle={tabSubjectHandle} isSubject={isSubject}
        isListen={true} originalText={<Article pageData={pageDataCache[pageDataIndex]} isAI={isAI}></Article>}
        noteHandle={noteHandle} showNotePanel={showNotePanel}
        submitHandle={submitHandle} clearDialogHandle={clearDialogHandle} aiContent={aiContent}
        notePanel={<NotePanel showNotePanel={showNotePanel} highLightInfo={highLightInfo} noteHandle={noteHandle}
          updateNoteHandle={updateDialogInfo} deleteNoteHandle={deleteNoteHandle}></NotePanel>}>
        {
          node
          // pageDataCache.map((pageDataItem, index: number) => {
          //   return (
          //     pageDataItem ? 
          //       <MainContent key={pageDataItem.question.paper_id} 
          //         question={pageDataItem.question.question} 
          //         currentFocus={currentFocus} 
          //         saveAnswerHandle={saveAnswerHandle}
          //         answerMap={answerMap}
          //         pageData={pageDataItem}
          //         loadAnswered={loadAnswered}
          //         feedback={resultMap}
          //         isAI={isAI}
          //         index={index}
          //         boxStyle={{display: pageDataIndex === index ? '' : 'none'}}
          //         showDragField={showDragField}
          //         setShowDragFieldHandle={setShowDragFieldHandle}
          //         setFocusIndexHandle={setFocusIndexHandle}></MainContent>
          //       : null
          //   );
          // })
        }
      </Main>
      <Bottom limits={limits} answerAll={answerAll} currentFocus={currentFocus} setPanelHandle={setPanelHandle} pageDataIndex={pageDataIndex} setFocusIndexHandle={setFocusIndexHandle} showNotePanel={showNotePanel}></Bottom>
      <Loading show={showLoading}></Loading>
    </div>
  );
}
